import type { FunnelStage, Lead } from '../../../types';

export type PipelineSegmentTone = 'brand' | 'success';

export interface PipelineSegment {
  id: string;
  weight: number;
  tone: PipelineSegmentTone;
}

export interface PipelineSummary {
  totalPipeline: number;
  negotiationsCount: number;
  wonValue: number;
  conversionRate: number;
  segments: PipelineSegment[];
}

const isOpen = (lead: Lead) => (lead.status ?? 'open') === 'open';
const isWon = (lead: Lead) => lead.status === 'won';
const isLost = (lead: Lead) => lead.status === 'lost';

export function computePipelineSummary(
  leads: Lead[],
  stages: FunnelStage[],
): PipelineSummary {
  if (leads.length === 0) {
    return {
      totalPipeline: 0,
      negotiationsCount: 0,
      wonValue: 0,
      conversionRate: 0,
      segments: [],
    };
  }

  const openLeads = leads.filter(isOpen);
  const wonLeads = leads.filter(isWon);
  const lostLeads = leads.filter(isLost);

  const totalPipeline = openLeads.reduce((sum, l) => sum + (l.value || 0), 0);
  const wonValue = wonLeads.reduce((sum, l) => sum + (l.value || 0), 0);
  const closed = wonLeads.length + lostLeads.length;
  const conversionRate = closed === 0 ? 0 : wonLeads.length / closed;

  const orderedStages = [...stages].sort(
    (a, b) => (a.order ?? 0) - (b.order ?? 0),
  );

  const valueByStage = new Map<string, number>();
  const countByStage = new Map<string, number>();
  for (const s of orderedStages) {
    valueByStage.set(s.id, 0);
    countByStage.set(s.id, 0);
  }
  for (const lead of openLeads) {
    if (!valueByStage.has(lead.stage)) continue;
    valueByStage.set(lead.stage, (valueByStage.get(lead.stage) || 0) + (lead.value || 0));
    countByStage.set(lead.stage, (countByStage.get(lead.stage) || 0) + 1);
  }

  const openValueSum = [...valueByStage.values()].reduce((a, b) => a + b, 0);
  const useCount = openValueSum === 0 && wonValue === 0;

  const segments: PipelineSegment[] = [];
  for (const s of orderedStages) {
    const weight = useCount
      ? countByStage.get(s.id) || 0
      : valueByStage.get(s.id) || 0;
    if (weight > 0) {
      segments.push({ id: s.id, weight, tone: 'brand' });
    }
  }
  const wonWeight = useCount ? wonLeads.length : wonValue;
  if (wonWeight > 0) {
    segments.push({ id: 'won', weight: wonWeight, tone: 'success' });
  }

  return {
    totalPipeline,
    negotiationsCount: leads.length,
    wonValue,
    conversionRate,
    segments,
  };
}
